import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ExploreIcon from "@mui/icons-material/Explore";
import Logo from "../assets/IZ-Logo.png";
import ProfileImage from "../assets/UserProfile.jpg";
import { AuthContext } from "../context/auth-context";
import { useProfile } from "../context/ProfileContext";
import { useTour } from "../context/TourContext";
import { TOURS } from "../context/TourContext";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const { profileImageUrl } = useProfile();
  const { startTour } = useTour();
  const [anchorEl, setAnchorEl] = useState(null);
  const [exploreAnchorEl, setExploreAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const exploreOpen = Boolean(exploreAnchorEl);

  const isProfilePage = location.pathname === "/profile";
  const isEmployee = user?.role === "EMPLOYEE";

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExploreOpen = (event) => {
    setExploreAnchorEl(event.currentTarget);
  };

  const handleExploreClose = () => {
    setExploreAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate("/profile");
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleLogoClick = () => {
    navigate("/home");
  };

  const handleTourStart = (tourId) => {
    handleExploreClose();
    startTour(tourId);
  };

  const handleHomeClick = () => {
    if (!user) return;
    
    switch (user.role) {
      case "ADMIN":
        navigate("/dashboard");
        break;
      case "FINANCIAL_ADMIN":
        navigate("/financial-dashboard");
        break;
      case "MANAGER":
        navigate("/manager-dashboard");
        break;
      case "EMPLOYEE":
      case "CONSULTANT":
      case "INTERN":
      default:
        navigate("/home");
        break;
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "#ffffff",
        color: "#000000",
        boxShadow:
          "0px 4px 10px rgba(0, 0, 0, 0.2), 0 2px 0 0 rgb(0, 216, 255)",
        zIndex: 40,
        top: 0,
      }}
    >
      <Toolbar
        sx={{
          maxWidth: "1200px",
          width: "100%",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 1, md: 3 },
        }}
      >
        {/* Logo and Title - Left side */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
          }}
          onClick={handleLogoClick}
        >
          <Box
            component="img"
            src={Logo}
            alt="IZSOFTWARE HUB Logo"
            sx={{
              height: { xs: "20px", md: "55px" },
              transition: "0.3s ease-in-out",
            }}
          />
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "0.8rem", md: "1.5rem" },
              color: "#000000",
              transition: "0.3s ease-in-out",
            }}
          >
            IZHUB
          </Typography>
        </Box>

        {/* Right side */}
        {isAuthenticated && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isProfilePage && (
              <Button
                onClick={handleHomeClick}
                startIcon={<HomeIcon />}
                sx={{
                  color: "#009db9",
                  border: "1px solid #009db9",
                  borderRadius: "20px",
                  px: 3,
                  py: 0.8,
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                  "&:hover": {
                    backgroundColor: "#009db9",
                    color: "#ffffff",
                    borderColor: "#009db9",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Dashboard
              </Button>
            )}

            {isEmployee && (
              <>
                <Button
                  onClick={handleExploreOpen}
                  startIcon={<ExploreIcon />}
                  sx={{
                    color: "#009db9",
                    border: "1px solid #009db9",
                    borderRadius: "20px",
                    px: 3,
                    py: 0.8,
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: { xs: "0.8rem", md: "0.9rem" },
                    "&:hover": {
                      backgroundColor: "#009db9",
                      color: "#ffffff",
                      borderColor: "#009db9",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Explore
                </Button>
                <Menu
                  anchorEl={exploreAnchorEl}
                  open={exploreOpen}
                  onClose={handleExploreClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 220,
                      borderRadius: 2,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    }
                  }}
                >
                  <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600 }}>
                    <Typography variant="caption" color="textSecondary">
                      FEATURE TOURS
                    </Typography>
                  </MenuItem>
                  <Divider />
                  {Object.values(TOURS).map((tour) => (
                    <MenuItem
                      key={tour.id}
                      onClick={() => handleTourStart(tour.id)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: '#f0f9ff',
                        }
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {tour.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {tour.steps.length} steps • interactive guide
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            <IconButton onClick={handleMenuOpen}>
              <Box
                component="img"
                src={profileImageUrl || ProfileImage}
                alt="Profile"
                sx={{
                  width: { xs: 40, md: 55 },
                  height: { xs: 40, md: 55 },
                  borderRadius: "50%",
                  objectFit: "cover",
                  transition: "0.3s ease-in-out",
                }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Log Out</MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;